import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  PLANNER_END_CONDITION_TYPE,
  PLANNER_END_CONDITIONS,
  PLANNER_SCHEDULE_TYPE,
  PLANNER_SCHEDULE_TYPES,
  PLANNER_SCOPE_TG_NOTIF_DISABLED,
  PLANNER_STATUS,
  PLANNER_TIME_UNIT,
  PLANNER_TIME_UNITS,
  PLANNER_WEEK_DAYS,
  PlannerActionsDropdownItem,
  PlannerActionType,
  PlannerForm,
  PlannerItem,
  PlannerPayload,
  PlannerSidebarData,
  PlannerSidebarMode,
  PriceChangeForm,
  PlannerUpsertPayload,
} from './planner-v2.interface';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseService } from '../../../../../services/base.service';
import { getMinTime, todayDate } from '../../../../../helpers/utils';
import { futureTimeValidator } from '../../../../../validators/future-time.validator';
import { futureDateValidator } from '../../../../../validators/future-date.validator';
import { RequestHelper } from '../../../../../services/request-helper.service';
import { Subject } from 'rxjs';
import { NotificationService } from '../../../../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { PlannerService } from '../../../../../services/planner.service';
import moment from 'moment-timezone';
import { PLANNER_ACTIONS_REGISTRY } from '../../../../../interfaces/planner-table.interface';
import {
  BulkChangePriceMode,
  PLANNER_OLX_ACTION,
} from '../../../../../modules/crm/power-olx-v2/interfaces/power-olx-v2.interface';

@Component({
  selector: 'app-planner-v2',
  templateUrl: './planner-v2.component.html',
  styleUrl: './planner-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PlannerV2Component implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject<void>();

  @Input() payload!: PlannerSidebarData;
  @Output() close = new EventEmitter<string>();

  protected plannerForm!: FormGroup<PlannerForm>;
  protected mode!: PlannerSidebarMode;
  protected actions!: PlannerActionsDropdownItem[];
  protected selectedAction?: PlannerActionsDropdownItem;
  protected isDraft!: boolean;
  protected existingPlanners: PlannerItem[] = [];
  protected loaders = {
    planner_upsert: false,
  };
  protected readonly priceChangeModes = [
    { value: BulkChangePriceMode.IncreaseAmount, name: 'bulk_change_price.increase_amount' },
    { value: BulkChangePriceMode.DecreaseAmount, name: 'bulk_change_price.decrease_amount' },
    { value: BulkChangePriceMode.IncreasePercent, name: 'bulk_change_price.increase_percent' },
    { value: BulkChangePriceMode.DecreasePercent, name: 'bulk_change_price.decrease_percent' },
  ];

  constructor(
    private readonly requestHelper: RequestHelper,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
    private readonly plannerService: PlannerService,
    protected readonly baseService: BaseService,
  ) {}

  ngOnInit(): void {
    this.initData();
    this.initForm();
    this.initExistingPlanners();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initData(): void {
    this.mode = this.payload.mode;
    this.actions = this.payload.actions;
  }

  private initForm(): void {
    const selectedDate = () => this.plannerForm?.controls.schedule?.controls.start_date.value;

    const scheduleType = this.payload.schedule?.type || PLANNER_SCHEDULE_TYPE.ONCE;

    const extractedTime = this.payload.schedule?.start_date
      ? moment(this.payload.schedule.start_date).local().format('HH:mm')
      : (this.payload.schedule?.time ?? moment().add(30, 'minutes').format('HH:mm'));

    const scheduleGroup = new FormGroup({
      type: new FormControl(scheduleType, Validators.required),
      start_date: new FormControl(
        this.payload.schedule?.start_date
          ? moment.utc(this.payload.schedule.start_date).local().utc().toISOString()
          : moment().add(30, 'minutes').toISOString(),
        [Validators.required, futureDateValidator],
      ),
      time: new FormControl(extractedTime, { validators: [Validators.required, futureTimeValidator(selectedDate)] }),
      ...(this.payload.payloads.length > 1 && {
        step: new FormGroup({
          number: new FormControl(this.payload.schedule?.step?.number || null),
          unit: new FormControl(this.payload.schedule?.step?.unit || null),
        }),
      }),
    });

    this.plannerForm = new FormGroup<PlannerForm>({
      scope: new FormControl(this.payload.scope, Validators.required),
      title: new FormControl(this.payload.title || null, Validators.required),
      status: new FormControl(this.payload.status || null),
      action: new FormControl(this.payload.action || this.actions[0].value, Validators.required),
      schedule: scheduleGroup,
      payloads: new FormControl(this.payload.payloads),
      planner_id: new FormControl(this.payload.planner_id || null),
    });

    this.plannerForm.controls.schedule.controls.start_date.valueChanges.subscribe(() => {
      this.plannerForm.controls.schedule.controls.time.updateValueAndValidity();
    });

    this.selectedAction = this.actions.find((action) => action.value === this.plannerForm.controls.action.value);
    this.plannerForm.controls.action.valueChanges.subscribe((value) => {
      this.selectedAction = this.actions.find((action) => action.value === value);
      this.togglePriceChangeControls(value);
    });

    this.togglePriceChangeControls(this.plannerForm.controls.action.value);

    if (scheduleType === PLANNER_SCHEDULE_TYPE.CUSTOM) {
      this.onToggleCustomFields(PLANNER_SCHEDULE_TYPE.CUSTOM);
    }

    this.isDraft = this.plannerForm.controls.status.value === PLANNER_STATUS.DRAFT;

    if (this.mode === PlannerSidebarMode.Update && this.payload.occurrences_count) {
      this.plannerForm.controls.schedule.controls.start_date.disable();
      this.plannerForm.controls.schedule.controls.start_date.clearValidators();
      this.plannerForm.controls.schedule.controls.time.disable();
      this.plannerForm.controls.schedule.controls.time.clearValidators();
    }
  }

  private initExistingPlanners(): void {
    const body = this.payload?.check_existing;
    if (!body) return;

    this.requestHelper.handle<PlannerItem[]>(
      this.plannerService.getExistingPlannersBySearch(body),
      (planners) => {
        this.existingPlanners = planners ?? [];
      },
      {
        key: 'planner_get_existing_by_payload',
        destroy$: this.destroy$,
      },
    );
  }

  private togglePriceChangeControls(action: PlannerActionType | null): void {
    const hasControl = this.plannerForm.contains('price_change');
    if (action === PLANNER_OLX_ACTION.CHANGE_PRICE) {
      const payloads = this.plannerForm.controls.payloads.value;
      const firstPayload = payloads?.[0];
      const existing = this.plannerForm.controls.price_change;

      const mode = existing?.controls.mode.value || firstPayload?.mode || BulkChangePriceMode.IncreaseAmount;
      const value = existing?.controls.value.value ?? firstPayload?.value ?? null;

      if (!hasControl) {
        const group = new FormGroup<PriceChangeForm>({
          mode: new FormControl<BulkChangePriceMode | null>(mode, Validators.required),
          value: new FormControl<number | null>(value, [Validators.required, Validators.min(0.01)]),
        });
        group.controls.mode.valueChanges.subscribe((m) => this.updatePriceChangeValidators(group, m));
        this.plannerForm.addControl('price_change', group);
        this.updatePriceChangeValidators(group, mode);
      } else {
        const group = this.plannerForm.controls.price_change!;
        group.controls.mode.setValue(mode);
        group.controls.value.setValue(value);
        this.updatePriceChangeValidators(group, mode);
      }
    } else if (hasControl) {
      this.plannerForm.removeControl('price_change');
    }
  }

  private updatePriceChangeValidators(group: FormGroup<PriceChangeForm>, mode: BulkChangePriceMode | null): void {
    const valueControl = group.controls.value;
    const validators = [Validators.required, Validators.min(0.01)];

    if (mode === BulkChangePriceMode.IncreasePercent || mode === BulkChangePriceMode.DecreasePercent) {
      validators.push(Validators.max(500));
    }

    valueControl.setValidators(validators);
    valueControl.updateValueAndValidity({ emitEvent: false });
  }

  protected onToggleCustomFields(type: PLANNER_SCHEDULE_TYPE): void {
    const schedule = this.plannerForm.controls.schedule as FormGroup;

    [
      'repeat',
      'end_condition',
      'days_of_week',
      'days_of_month',
      'timezone',
      'custom_cron',
      'max_occurrences',
      'until_date',
    ].forEach((field) => schedule.removeControl(field));

    if (type !== PLANNER_SCHEDULE_TYPE.CUSTOM) return;

    schedule.addControl(
      'repeat',
      new FormGroup({
        unit: new FormControl(this.payload.schedule?.repeat?.unit || PLANNER_TIME_UNIT.DAY, Validators.required),
        every: new FormControl(this.payload.schedule?.repeat?.every || 1, [Validators.required, Validators.min(1)]),
      }),
    );

    const endType = this.payload.schedule?.end_condition?.type ?? PLANNER_END_CONDITION_TYPE.NEVER;
    schedule.addControl(
      'end_condition',
      new FormGroup({
        type: new FormControl(endType, Validators.required),
      }),
    );

    this.onChangeEndCondition(endType);

    schedule.addControl('days_of_week', new FormControl(this.payload.schedule?.days_of_week || []));
    schedule.addControl('days_of_month', new FormControl(this.payload.schedule?.days_of_month || []));
    schedule.addControl('timezone', new FormControl(this.payload.schedule?.timezone || null));
    schedule.addControl('custom_cron', new FormControl(this.payload.schedule?.custom_cron || null));
  }

  protected onToggleStatus(status: PLANNER_STATUS): void {
    let newStatus: PLANNER_STATUS | null = status;

    if (this.plannerForm.controls.status.value === status) {
      newStatus = (this.payload.status !== PLANNER_STATUS.DRAFT && this.payload.status) || null;
    }

    this.plannerForm.controls.status.setValue(newStatus);
  }

  protected onToggleCheckbox(event: boolean, key: string): void {
    const formatedPayloads: PlannerPayload[] = this.plannerForm.controls.payloads.value!.map((v) => ({
      ...v,
      [key]: event,
    }));
    this.plannerForm.controls.payloads.setValue(formatedPayloads);
  }

  protected onChangeEndCondition(type: PLANNER_END_CONDITION_TYPE): void {
    const endCondition = this.plannerForm.controls.schedule.controls.end_condition as FormGroup;
    if (!endCondition) return;

    endCondition.removeControl('max_occurrences');
    endCondition.removeControl('until_date');

    if (type === PLANNER_END_CONDITION_TYPE.AFTER) {
      endCondition.addControl(
        'max_occurrences',
        new FormControl(this.payload.schedule?.end_condition?.max_occurrences || 1, [
          Validators.required,
          Validators.min(1),
        ]),
      );
    }

    // TODO: make until_date validation
    if (type === PLANNER_END_CONDITION_TYPE.ON_DATE) {
      endCondition.addControl(
        'until_date',
        new FormControl(
          this.payload.schedule?.end_condition?.until_date
            ? moment.utc(this.payload.schedule.end_condition.until_date).local().utc().toISOString()
            : null,
          [Validators.required, futureDateValidator],
        ),
      );
    }
  }

  protected onSave(): void {
    this.plannerForm.controls.schedule.controls.time.updateValueAndValidity();
    this.plannerForm.controls.schedule.controls.start_date.updateValueAndValidity();
    this.plannerForm.controls.schedule.controls.end_condition?.controls.until_date?.updateValueAndValidity();
    this.plannerForm.controls.price_change?.updateValueAndValidity();
    this.plannerForm.controls.price_change?.controls.value.updateValueAndValidity();

    if (this.plannerForm.invalid) {
      this.plannerForm.markAllAsTouched();
      this.plannerForm.controls.price_change?.markAllAsTouched();
      return;
    }

    const successNotifText =
      this.mode === PlannerSidebarMode.Create
        ? 'notifications_texts.success.successfully_planned'
        : 'notifications_texts.success.successfully_updated';

    const payload: PlannerUpsertPayload = this.preparePayload();

    if (payload.action === PLANNER_OLX_ACTION.CHANGE_PRICE && this.plannerForm.controls.price_change) {
      const priceChange = this.plannerForm.controls.price_change.getRawValue();
      payload.payloads = (payload.payloads || []).map((item: PlannerPayload) => ({
        ...item,
        mode: priceChange.mode || undefined,
        value: priceChange.value || undefined,
      }));
    }

    this.submitPlanner(payload, successNotifText);
  }

  private submitPlanner(payload: PlannerUpsertPayload, successNotifText: string): void {
    this.requestHelper.handle<void>(
      this.plannerService.upsertPlanners(payload),
      () => {
        this.notificationService.showSuccess(
          this.translate.instant('notifications_texts.success.success'),
          this.translate.instant(successNotifText),
        );
        this.onCloseSidebar('success');
      },
      {
        key: 'planner_upsert',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'planner_upsert',
        defaultErrorMessage: 'notifications_texts.error.failed_to_upsert_planner',
      },
    );
  }

  private preparePayload(): PlannerUpsertPayload {
    const payload = this.plannerForm.getRawValue() as PlannerUpsertPayload;
    const priceChange = this.plannerForm.controls.price_change?.getRawValue();

    const dateStr = moment(payload.schedule.start_date).format('MM/DD/YYYY');
    const timeStr = payload.schedule.time;
    payload.schedule.start_date = moment
      .tz(`${dateStr} ${timeStr}`, 'MM/DD/YYYY HH:mm', moment.tz.guess())
      .utc()
      .toISOString();

    if (payload.schedule.end_condition?.until_date) {
      payload.schedule.end_condition.until_date = moment(payload.schedule.end_condition.until_date)
        .tz(moment.tz.guess())
        .endOf('day')
        .utc()
        .toISOString();
    }

    if (priceChange) {
      payload.payloads = (payload.payloads || []).map((item: PlannerPayload) => ({
        ...item,
        mode: priceChange.mode || undefined,
        value: priceChange.value || undefined,
      }));
    }

    return payload;
  }

  protected onCloseSidebar(status?: string): void {
    this.close.emit(status);
  }

  protected readonly scheduleTypes = PLANNER_SCHEDULE_TYPES;
  protected readonly weekDays = PLANNER_WEEK_DAYS;
  protected readonly endConditions = PLANNER_END_CONDITIONS;
  protected readonly timeUnits = PLANNER_TIME_UNITS;
  protected readonly PLANNER_SCHEDULE_TYPE = PLANNER_SCHEDULE_TYPE;
  protected readonly PLANNER_STATUS = PLANNER_STATUS;
  protected readonly PlannerSidebarMode = PlannerSidebarMode;
  protected readonly todayDate = todayDate;
  protected readonly getMinTime = getMinTime;
  protected readonly PLANNER_SCOPE_TG_NOTIF_DISABLED = PLANNER_SCOPE_TG_NOTIF_DISABLED;
  protected readonly PLANNER_ACTIONS_REGISTRY = PLANNER_ACTIONS_REGISTRY;
  protected readonly BulkChangePriceMode = BulkChangePriceMode;
  protected readonly PLANNER_OLX_ACTION = PLANNER_OLX_ACTION;
}
